import React from 'react';
import { t } from 'i18next';
import { status } from '../../../enums/statusEnum';
import {
    getMaterialRequestTableStatusId,
    getMovementDirectionOptionByValue,
} from '../../Dashboard/MaterialControl/Model/Model';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * QR code types handled by the platform, mirrors the backend QRCodeType enum.
 */
export const QR_CODE_TYPE = {
    MATERIAL_REQUEST: 9,
    MATERIAL_CONTROL: 10,
    VEHICLE_MOVEMENT: 11,
};

/**
 * Scanner states.
 *
 * IDLE: The camera is off, waiting for the user to start the scan.
 * SCANNING: The camera is on and looking for a QR code.
 * RESOLVING: A QR code was read and its identifier is being decrypted.
 * BLOCKED: The camera could not be started, usually a denied permission.
 */
export const SCANNER_STATUS = {
    IDLE: 'idle',
    SCANNING: 'scanning',
    RESOLVING: 'resolving',
    BLOCKED: 'blocked',
};

/**
 * Status label and status chip style for every scanner state.
 */
export const SCANNER_STATUS_ACTIONS = {
    [SCANNER_STATUS.IDLE]: {
        label: 'scanner_idle',
        tone: 'neutral',
        loading: false,
    },

    [SCANNER_STATUS.SCANNING]: {
        label: 'scanner_scanning',
        tone: 'active',
        loading: false,
    },

    [SCANNER_STATUS.RESOLVING]: {
        label: 'scanner_resolving',
        tone: 'active',
        loading: true,
    },

    [SCANNER_STATUS.BLOCKED]: {
        label: 'scanner_blocked',
        tone: 'error',
        loading: false,
    },
};

/**
 * Identifier of the html element where the camera preview is mounted.
 */
export const SCANNER_REGION_ID = 'div_sc_scanner_region';

/**
 * Camera configuration used by Html5Qrcode.
 *
 * No 'qrbox' is set on purpose: with a scan box the library crops the frame
 * using the size the preview had when the camera started, and any later layout
 * change leaves the crop out of place, so the whole frame is scanned instead.
 * The native barcode detector is used when the browser supports it because it
 * reads dense codes faster.
 */
export const SCANNER_CONFIG = {
    fps: 10,
    videoConstraints: {
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 },
    },
    experimentalFeatures: {
        useBarCodeDetectorIfSupported: true,
    },
};

/**
 * Milliseconds the camera preview is waited for before it is given up on.
 *
 * html5-qrcode shows the preview with a play() it never waits for, so the wait
 * is bounded to keep the camera from being locked when the video never starts.
 */
export const SCANNER_PREVIEW_PLAY_TIMEOUT = 3000;

/**
 * Matches the material request or material control identifier returned by the decrypt service.
 */
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Letters the folio is drawn with while it is selected.
 *
 * The code of the folio opens with what names the series it belongs to, so the
 * card is drawn with those letters and falls back to the first characters of
 * the code when it carries none.
 *
 * @param {string} code - Code of the folio.
 *
 * @returns {string} Up to two letters, in upper case.
 */
export const getGateFolioInitials = (code) => {
    const value = (code || '').trim();
    const letters = value.replace(/[^a-z]/gi, '');

    return (letters || value).slice(0, 2).toUpperCase();
};

/**
 * Separator the values of a folio are read one after another with.
 */
const FOLIO_SEPARATOR = ' · ';

/**
 * Name the folio is read with, its code followed by the request it was captured with.
 *
 * The folio is offered and selected with the same name, so the option the gate
 * picks reads the way the card it is picked into does.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {string} Name of the folio.
 */
export const getGateFolioName = (item) =>
    [item?.code, item?.title].filter(Boolean).join(FOLIO_SEPARATOR);

/**
 * Direction the folio moves its materials in, read as the card writes it.
 *
 * The direction is named and then described, so the gate reads what the folio
 * does before it reads the status it waits with. The description is written as
 * a sentence of its own, so the stop it closes with is left out.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {string} Direction of the folio, empty when it declares none.
 */
export const getGateFolioDirection = (item) => {
    // The service stores the direction as text, so the folio is matched by the
    // value it was registered with and not by the key the forms select it with.
    const direction = getMovementDirectionOptionByValue(item?.movementDirection);

    if (!direction) return '';

    return `${t(direction.trans)} — ${t(direction.description).replace(/\.$/, '')}`;
};

/**
 * Status the folio is read with at the gate.
 *
 * A folio already approved is read with the status it carries at the gate, the
 * same one its listing shows it with.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {string} Translation key of the status, empty when the folio carries none.
 */
export const getGateFolioStatusTrans = (item) => status[getMaterialRequestTableStatusId(item)] || '';

/**
 * Description the folio is read under its name with.
 *
 * The folio is described the same way it is offered and selected, so the option
 * the gate picks reads as the card it is picked into does.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {string} Direction and status of the folio, empty when it carries neither.
 */
export const getGateFolioDescription = (item) => {
    const statusTrans = getGateFolioStatusTrans(item);

    return [getGateFolioDirection(item), statusTrans && t(statusTrans)]
        .filter(Boolean)
        .join(FOLIO_SEPARATOR);
};

/**
 * Maps the folios the search returns into the shape the chip search reads them with.
 *
 * The option is named the same way the card it is picked into is, its code and
 * the request it was captured with, and it is described by the direction the
 * folio moves its materials in and the status it is read with at the gate.
 *
 * @param {Array<Object>} list - Folios the search returned.
 *
 * @returns {Array<Object>} Folios as options of the chip search.
 */
export const gateFolioListToLabel = (list) => {
    if (!list) return [];

    return list.map((item) => {
        const description = getGateFolioDescription(item);

        return {
            id: item?.id,
            label: getGateFolioName(item),
            // The option is drawn with the same letters the card it is picked
            // into is, so the folio is recognized before its name is read.
            Icon: () => (
                <span
                    className={'d-inline-flex flex-shrink-0 align-items-center justify-content-center w-40px h-40px background-primary-25 border-radius-8px fs-12px ibl-font-w-700 font-color-primary-800'}>
                    {getGateFolioInitials(item?.code)}
                </span>
            ),
            // The folio is opened with the same values the QR code carries, so the
            // record it was read from travels with the option that was selected.
            item,
            subSection: () => (
                description ? (
                    <div className={'col-12 p-0'}>
                        <p className={'fs-12px ibl-font-w-400 font-color-shades-600 mb-0 text-break'}>
                            {description}
                        </p>
                    </div>
                ) : null
            ),
        };
    });
};
